import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { authService, companyService } from "./services/api";
import {
  getAuthItem,
  getAuthJson,
  setAuthItem,
} from "./utils/authStorage";
import EssProxyPage from "./pages/EssProxyPage";
import CandidateProfileInvitePage from "./components/CandidatePortal/CandidateProfileInvitePage";

// Admin Components
import Home from "./components/Admin/Home";
import Navbar from "./components/Admin/Navbar";
import Sidebar from "./components/Admin/Sidebar";
import LoginPage from "./components/auth/LoginPage";

// Eager imports: React.lazy + dynamic import() was resolving to undefined default
// exports at runtime (code-split chunk issue). Static imports guarantee a component.
import Attendance from "./components/Admin/Attendance";
import Employee from "./components/Admin/Employee";
import Payroll from "./components/Admin/Payroll";
import Reports from "./components/Admin/Reports";
import GlobalSettingsModal from "./components/Admin/GlobalSettingsModal";

// Employee Components
import EmployeeAttendance from "./Employee/EmployeeAttendance";
import EmployeeHome from "./Employee/EmployeeHome";
import EmployeePayroll from "./Employee/EmployeePayroll";
import EmployeeProfile from "./Employee/EmployeeProfile";
import EmployeeReports from "./Employee/EmployeeReports";
import MyTeam from "./Employee/MyTeam";
import MyAssets from "./Employee/MyAssets";
import "./App.css";
// Removed EmployeeNavbar - using single Navbar for both admin and employee
import Toast from "./Toast";
import Masters from "./components/Admin/masters/Masters";
import Configuration from "./components/Admin/attendance/components/Configuration";
import AuthHandler from "./components/auth/AuthHandler"; // New component for handling authentication
import HelpdeskConfigModal from "./components/Admin/HelpdeskConfigModal";
import ActivityConsole from "./components/Admin/ActivityConsole";
import ActivityLogMonitor from "./components/Admin/settings/ActivityLogMonitor";
import NotificationEmailsSettings from "./components/Admin/settings/NotificationEmailsSettings";
import WorkspacePage from "./components/Admin/workspace/WorkspacePage";
import StatutoryReportsPage from "./components/Admin/statutory/StatutoryReportsPage";
import Hiring from "./components/Admin/Hiring";
import Assets from "./components/Admin/Assets";
import { PermissionsProvider, clearRbacSnapshot } from "./rbac/PermissionsContext";
import RouteGate, { WorkspaceRouteGate } from "./rbac/RouteGate";
import { refreshProductThemeForColorScheme } from "./design-system/themes";
import { ProductThemeFab } from "./design-system";

function ProtectedRoute({ children, allowedUserType }) {
  const navigate = useNavigate();
  const isAuthenticated = getAuthItem("isAuthenticated") === "true";
  const userType = getAuthItem("userType");
  const allowedTypes = useMemo(
    () =>
      Array.isArray(allowedUserType)
        ? allowedUserType
        : [allowedUserType],
    [allowedUserType]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else if (!allowedTypes.includes(userType)) {
      navigate("/home");
    }
  }, [isAuthenticated, navigate, allowedTypes, userType]);

  return isAuthenticated && allowedTypes.includes(userType) ? children : null;
}

function URLParameterHandler({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const companyId = urlParams.get("companyId");
    const superAdminID = urlParams.get("superAdminID");
    const password = urlParams.get("password");

    console.log(companyId, superAdminID, password);

    if (companyId && superAdminID && password) {
      // Auto-login with URL parameters
      onLogin("admin", companyId, superAdminID);

      // Clear URL parameters after login
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      // Navigate to unified home route
      navigate("/home");
    }
  }, [location, onLogin, navigate]);

  return null;
}

const COMPANY_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Refresh company data once after login / reload (not on every route change).
function CompanyDataRefresher() {
  const isAuthenticated = getAuthItem("isAuthenticated") === "true";

  useEffect(() => {
    const refreshCompanyData = async () => {
      if (!isAuthenticated) return;

      try {
        const currentUser = getAuthJson("currentUser");
        if (currentUser?.companyId) {
          const cachedCompany = getAuthItem("companyData");
          const cachedTs = Number(getAuthItem("companyDataRefreshedAt") || 0);
          let cachedCompanyId = null;
          try {
            cachedCompanyId = cachedCompany ? JSON.parse(cachedCompany)?.id : null;
          } catch {
            cachedCompanyId = null;
          }
          const isFresh =
            !!cachedCompany &&
            String(cachedCompanyId || "") === String(currentUser.companyId) &&
            Number.isFinite(cachedTs) &&
            Date.now() - cachedTs < COMPANY_REFRESH_INTERVAL_MS;
          if (isFresh) return;

          await companyService.refreshCompanyData(currentUser.companyId);
          setAuthItem("companyDataRefreshedAt", String(Date.now()));
        }
      } catch (error) {
        console.warn("⚠️ Company data refresh skipped:", error);
        // Don't throw error - refresh is optional
      }
    };

    // Refresh only once per authenticated session (with staleness check)
    refreshCompanyData();
  }, [isAuthenticated]);

  return null;
}

function App() {
  const [, setCurrentPage] = useState("Home");
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return getAuthItem("isAuthenticated") === "true";
  });
  const [userType, setUserType] = useState(() => {
    return getAuthItem("userType") || "";
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [companyName, setCompanyName] = useState(() => {
    const company = getAuthJson("companyData");
    return company?.name || "";
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    // Re-apply brand theme vars for light/dark after class toggle.
    refreshProductThemeForColorScheme();
  }, [darkMode]);

  // Update company name when company data changes
  useEffect(() => {
    const company = getAuthJson("companyData");
    if (company) {
      setCompanyName(company.name);
      // Update document title
      if (company && company.name) {
        document.title = company.name + " - PaceHRM";
      } else {
        document.title = "PaceHRM";
      }
    } else {
      document.title = "PaceHRM";
    }
  }, []);

  // Listen for localStorage changes to update title when company data changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "companyData") {
        try {
          if (e.newValue) {
            const company = JSON.parse(e.newValue);
            if (company && company.name) {
              document.title = company.name + " - PaceHRM";
              setCompanyName(company.name);
            } else {
              document.title = "PaceHRM";
              setCompanyName("");
            }
          } else {
            document.title = "PaceHRM";
            setCompanyName("");
          }
        } catch (error) {
          console.error("Error parsing company data:", error);
          document.title = "PaceHRM";
          setCompanyName("");
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode;
      localStorage.setItem("darkMode", String(newMode));
      return newMode;
    });
  }, []);

  const handleLogin = useCallback((type, companyId, superAdminID) => {
    setIsAuthenticated(true);
    setUserType(type);
    setAuthItem("isAuthenticated", "true");
    setAuthItem("userType", type);
    if (companyId !== undefined && companyId !== null) {
      setAuthItem("companyId", String(companyId));
    }
    if (superAdminID !== undefined && superAdminID !== null) {
      setAuthItem("superAdminID", String(superAdminID));
    }

    // Update company name after login
    const company = getAuthJson("companyData");
    if (company?.name) {
      setCompanyName(company.name);
    }
  }, []);

  const handleLogout = useCallback(() => {
    authService.logout();
    clearRbacSnapshot();
    setIsAuthenticated(false);
    setUserType("");
    setCompanyName("");
    window.location.href = "/login"; // Force redirect to login
  }, []);

  const renderAdminContent = (pageName) => {
    const props = {
      darkMode,
      setCurrentPage,
      toggleDarkMode,
    };

    switch (pageName) {
      case "Home":
        return <Home {...props} />;
      case "Masters":
        return <Masters {...props} />;
      case "Time & Attendance":
        return <Attendance {...props} />;
      case "Employee":
        return <Employee {...props} />;
      case "Payroll":
        return <Payroll {...props} />;
      case "Hiring":
        return <Hiring />;
      case "Assets":
        return <Assets />;
      case "Helpdesk":
        return <HelpdeskConfigModal variant="page" />;
      case "Reports":
        return <Reports {...props} />;
      case "Global Settings":
        return <GlobalSettingsModal initialTab="policy" />;
      case "Communication":
        return <GlobalSettingsModal initialTab="communication" />;
      case "User Role Management":
        return <GlobalSettingsModal initialTab="user_role_management" />;
      case "Configuration":
        return <Configuration darkMode={darkMode} />;
      case "Workspace":
        return <WorkspacePage />;
      case "Statutory Reports":
        return <StatutoryReportsPage darkMode={darkMode} />;
      case "Activity Console":
        return <ActivityConsole />;
      case "Activity Log Monitor":
        return <ActivityLogMonitor />;
      case "Notification Emails":
        return <NotificationEmailsSettings />;
      case "Requests":
      case "Approvals":
        return (
          <div
            className="h-full min-h-[calc(100dvh-5rem)] bg-[hsl(var(--ds-bg))]"
            aria-hidden
          />
        );
      default:
        return <Home {...props} />;
    }
  };

  // Single shell so Sidebar (Request / Approvals desk) stays mounted across
  // /admin/* navigations instead of remounting and bouncing to Home.
  const renderAdminLayout = (pageName) => renderAdminContent(pageName);

  // Renders employee page components (Profile/Attendance/Payroll/Reports)
  // inside the admin shell so only the middle content swaps while the top nav
  // and both side panels remain intact.
  const renderAdminShellWithEmployee = (employeeElement) => (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <Sidebar
        setCurrentPage={setCurrentPage}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />
      {employeeElement}
    </div>
  );

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <Router>
        <Toast />
        <div
          className={`flex h-screen overflow-hidden font-sans antialiased ${darkMode ? "dark" : ""}`}
        >
          <div className="flex w-full items-center justify-center bg-[hsl(var(--ds-bg))] dark:bg-[hsl(var(--ds-bg))]">
            <div className="text-center">
              <div
                className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-[hsl(var(--ds-border))] border-t-[hsl(var(--ds-primary))] dark:border-[hsl(var(--ds-border))] dark:border-t-[hsl(var(--ds-accent))]"
                aria-hidden
              />
              <p className="text-sm font-semibold text-[hsl(var(--ds-fg))] dark:text-[hsl(var(--ds-muted-fg))]">
                Processing authentication…
              </p>
            </div>
          </div>
        </div>
      </Router>
    );
  }

  return (
    <Router>
      <Toast />
      <PermissionsProvider>
      <AuthHandler
        setIsAuthenticated={setIsAuthenticated}
        setUserType={setUserType}
        setIsCheckingAuth={setIsCheckingAuth}
      />
      <URLParameterHandler onLogin={handleLogin} />
      <CompanyDataRefresher />
      {isAuthenticated &&
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/ess-proxy") && (
          <ProductThemeFab />
        )}
      <div
        className={`flex h-screen overflow-hidden font-sans antialiased ${darkMode ? "dark" : ""}`}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          {isAuthenticated &&
            typeof window !== "undefined" &&
            !window.location.pathname.startsWith("/ess-proxy") && (
            <>
              <Navbar
                darkMode={darkMode}
                handleLogout={handleLogout}
                isEmployeeView={userType === "employee"}
              />
            </>
          )}
          <main
            className="scrollbar-hide flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--ds-bg))] font-sans antialiased text-[hsl(var(--ds-fg))] transition-[padding] duration-300 dark:bg-[hsl(var(--ds-bg))] dark:text-[hsl(var(--ds-fg))]"
            style={
              isAuthenticated
                ? {
                    paddingLeft: "var(--admin-left-sidebar-width, 0px)",
                    paddingRight: "var(--admin-right-sidebar-width, 0px)",
                    paddingTop: "var(--admin-secondary-nav-height, 0px)",
                  }
                : undefined
            }
          >
            <div className="scrollbar-hide flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
            <Routes>
              {/* Login Route */}
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/home" replace />
                  ) : (
                    <LoginPage onLogin={handleLogin} darkMode={darkMode} />
                  )
                }
              />

              {/* Unified Home → admin shell (same Sidebar instance as Request desk) */}
              <Route
                path="/home"
                element={
                  isAuthenticated ? (
                    <Navigate to="/admin/home" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Admin Routes — one Sidebar for all pages so Request/Approvals stay open */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedUserType={["admin", "employee"]}>
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                      <Sidebar
                        setCurrentPage={setCurrentPage}
                        darkMode={darkMode}
                        toggleDarkMode={toggleDarkMode}
                      />
                      <Outlet />
                    </div>
                  </ProtectedRoute>
                }
              >
                <Route
                  path="home"
                  element={
                    <RouteGate module="home">
                      {renderAdminLayout("Home")}
                    </RouteGate>
                  }
                />
                <Route
                  path="requests"
                  element={renderAdminLayout("Requests")}
                />
                <Route
                  path="approvals"
                  element={renderAdminLayout("Approvals")}
                />
                <Route
                  path="workspace/:workspaceKey"
                  element={
                    <WorkspaceRouteGate>
                      {renderAdminLayout("Workspace")}
                    </WorkspaceRouteGate>
                  }
                />
                <Route
                  path="configuration"
                  element={
                    <RouteGate module="configuration">
                      {renderAdminLayout("Configuration")}
                    </RouteGate>
                  }
                />
                <Route
                  path="masters"
                  element={
                    <RouteGate module="configuration">
                      {renderAdminLayout("Masters")}
                    </RouteGate>
                  }
                />
                <Route
                  path="attendance"
                  element={
                    <RouteGate module="attendance">
                      {renderAdminLayout("Time & Attendance")}
                    </RouteGate>
                  }
                />
                <Route
                  path="employee"
                  element={
                    <RouteGate module="employee">
                      {renderAdminLayout("Employee")}
                    </RouteGate>
                  }
                />
                <Route
                  path="payroll"
                  element={
                    <RouteGate module="payroll">
                      {renderAdminLayout("Payroll")}
                    </RouteGate>
                  }
                />
                <Route
                  path="hiring"
                  element={
                    <RouteGate module="hiring">
                      {renderAdminLayout("Hiring")}
                    </RouteGate>
                  }
                />
                <Route
                  path="assets"
                  element={
                    <RouteGate module="assets">
                      {renderAdminLayout("Assets")}
                    </RouteGate>
                  }
                />
                <Route
                  path="communication"
                  element={
                    <RouteGate module="communication">
                      {renderAdminLayout("Communication")}
                    </RouteGate>
                  }
                />
                <Route
                  path="helpdesk"
                  element={
                    <RouteGate module="helpdesk">
                      {renderAdminLayout("Helpdesk")}
                    </RouteGate>
                  }
                />
                <Route
                  path="user-role-management"
                  element={
                    <RouteGate module="user_roles">
                      {renderAdminLayout("User Role Management")}
                    </RouteGate>
                  }
                />
                <Route
                  path="activity-console"
                  element={
                    <RouteGate
                      module="configuration"
                      screen="activity_console"
                      action="view"
                    >
                      {renderAdminLayout("Activity Console")}
                    </RouteGate>
                  }
                />
                <Route
                  path="activity-log-monitor"
                  element={
                    <RouteGate
                      module="workspaces"
                      screen="activity_log_monitor"
                      action="view"
                    >
                      {renderAdminLayout("Activity Log Monitor")}
                    </RouteGate>
                  }
                />
                <Route
                  path="notification-emails"
                  element={
                    <RouteGate
                      module="workspaces"
                      screen="notification_emails"
                      action="view"
                    >
                      {renderAdminLayout("Notification Emails")}
                    </RouteGate>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <RouteGate companyModule="reports">
                      {renderAdminLayout("Reports")}
                    </RouteGate>
                  }
                />
                <Route
                  path="statutory-reports"
                  element={
                    <RouteGate
                      module="statutory_reports"
                      screen="statutory_reports_hub"
                      action="view"
                    >
                      {renderAdminLayout("Statutory Reports")}
                    </RouteGate>
                  }
                />
                <Route
                  path="global-settings"
                  element={
                    <RouteGate
                      module="communication"
                      screen="company_policy"
                      action="view"
                    >
                      {renderAdminLayout("Global Settings")}
                    </RouteGate>
                  }
                />
                <Route index element={<Navigate to="home" replace />} />
              </Route>

              {/* Employee Routes - render inside admin shell so only the
                  middle content swaps while admin navbar + side panels stay. */}
              <Route
                path="/employee/*"
                element={
                  <ProtectedRoute allowedUserType={["employee", "admin"]}>
                    <Routes>
                      <Route
                        path="home"
                        element={
                          <RouteGate
                            module="home"
                            redirectTo="/employee/profile"
                          >
                            {renderAdminShellWithEmployee(<EmployeeHome />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="profile"
                        element={
                          <RouteGate
                            module="profile"
                            screen="org_details"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeeProfile />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="attendance"
                        element={
                          <RouteGate
                            module="ess_attendance"
                            screen="self_service"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeeAttendance />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="payroll"
                        element={
                          <RouteGate
                            module="ess_payroll"
                            screen="self_service"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeePayroll />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <RouteGate
                            module="ess_reports"
                            screen="reports"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeeReports />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="my-team"
                        element={renderAdminShellWithEmployee(<MyTeam />)}
                      />
                      <Route
                        path="my-assets"
                        element={
                          <RouteGate
                            module="ess_assets"
                            screen="self_service"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<MyAssets />)}
                          </RouteGate>
                        }
                      />
                    </Routes>
                  </ProtectedRoute>
                }
              />

              {/* ESS Proxy redemption page — public, no auth required */}
              <Route path="/ess-proxy" element={<EssProxyPage />} />

              {/* Candidate profile invite — token-gated, profile fill only */}
              <Route
                path="/candidate-profile/:token"
                element={<CandidateProfileInvitePage />}
              />

              {/* Default Route */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate to="/home" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>
            </div>
          </main>
        </div>
      </div>
      </PermissionsProvider>
    </Router>
  );
}

export default App;
